import { Fragment, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AiMarkdown from "@/components/AiMarkdown";
import PageHeader from "@/components/PageHeader";
import VoiceRecorder from "@/components/VoiceRecorder";
import { LiliHistory } from "@/components/LiliHistory";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, ShieldAlert, CheckCircle2, XCircle, Wrench, Volume2, Square, Download } from "lucide-react";
import { telechargerDossierInspection } from "@/lib/dossierInspection";
import { PlanApercu } from "@/components/PlanApercu";
import { EditBanner, MessageTools } from "@/components/MessageTools";
import { AttachButton, PiecesChips, retirerPiece } from "@/components/Attachments";
import { Apprentissage, stripRetenir } from "@/components/ZoeLearn";

const EXEMPLES = [
  "Combien de demandes simulateur sont au statut « nouvelle » ?",
  "Liste les courriels non partis et renvoie-les tous",
  "Crée un compte gestionnaire GE pour Marie Dupont (marie.dupont@exemple.fr), territoire Martinique",
  "Quelles notes de frais attendent une décision ?",
  "Qu'est-ce qui manque pour une inspection ? Prépare le dossier",
  "Inspection le 30/11 : crée le plan de régularisation pour la Martinique",
];

async function streamLili({ message, sessionId, editOf, pieces, onEvent }) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lili/chat/stream`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream",
               Authorization: `Bearer ${localStorage.getItem("oscopge_token") || ""}` },
    body: JSON.stringify({ message, session_id: sessionId, edit_of: editOf || undefined, pieces: pieces || [] }),
  });
  if (!res.ok || !res.body) {
    const raw = await res.text().catch(() => "");
    let detail = null;
    try { detail = JSON.parse(raw).detail; } catch { /* réponse non JSON */ }
    const msg = res.status === 403 ? "Vos droits ne permettent pas d'utiliser ZOE — Administration."
      : formatApiError(detail) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const line = buf.slice(0, idx).trim(); buf = buf.slice(idx + 2);
      if (line.startsWith("data:")) { try { onEvent(JSON.parse(line.slice(5).trim())); } catch { /* ignore */ } }
    }
  }
}

const PDF_APRES = { creer_devis: (id) => `/devis/${id}/pdf`, generer_pdf_contrat: (id) => `/contrats/${id}/pdf` };

function ActionCard({ action, onUpdate }) {
  const [motif, setMotif] = useState(action.args?.motif || "");
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = action.statut === "en_attente";
  const args = Object.entries(action.args || {}).filter(([k]) => !k.toLowerCase().includes("password"));

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lili/actions/${action.id}/confirm`, { motif: motif || null, confirmation_irreversible: sure });
      onUpdate({ ...action, statut: data.ok ? "executee" : "echec", resultat: data.resultat, http: data.http });
      data.ok ? toast.success("Action exécutée par ZOE") : toast.error(formatApiError(data.resultat?.detail) || `Refusé (${data.http})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.post(`/lili/actions/${action.id}/cancel`); onUpdate({ ...action, statut: "annulee" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const tempPwd = action.resultat?.temp_password;

  return (
    <div className={`rounded-lg border p-4 my-2 ${action.irreversible ? "border-red-200 bg-red-50/40" : "border-[#C5A059]/40 bg-[#C5A059]/5"}`} data-testid={`lili-action-${action.id}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        {action.irreversible ? <ShieldAlert className="h-4 w-4 text-red-600" /> : <Wrench className="h-4 w-4 text-[#A88846]" />}
        {action.libelle || action.tool.replaceAll("_", " ")}
        {action.irreversible && <span className="text-[10px] uppercase tracking-wider bg-red-600 text-white rounded px-1.5 py-0.5">irréversible</span>}
      </div>
      <p className="text-xs text-slate-600 mt-1">{action.description}</p>
      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
        {(action.affichage || args).map(([k, v]) => (<Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="text-slate-800 break-words">{String(v)}</dd></Fragment>))}
      </dl>
      {action.apercu?.taches && <PlanApercu plan={action.apercu} />}
      {action.statut === "executee" && action.resultat?.id && PDF_APRES[action.tool] && (
        <div className="mt-2"><PdfButton id={`action-${action.id}`} d={{ url: PDF_APRES[action.tool](action.resultat.id), nom: `${action.tool === "creer_devis" ? "Devis" : "Contrat"} ${action.resultat.numero || ""}`.trim() }} /></div>)}
      {pending && action.irreversible && (
        <div className="mt-3 space-y-2">
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (obligatoire)" rows={2} data-testid={`lili-action-motif-${action.id}`} />
          <label className="flex items-center gap-2 text-xs text-red-700">
            <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} data-testid={`lili-action-sure-${action.id}`} />
            Je confirme cette action irréversible
          </label>
        </div>
      )}
      {pending ? (
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={confirm} disabled={busy || (action.irreversible && (motif.trim().length < 3 || !sure))}
                  className="bg-[#C5A059] hover:bg-[#A88846] text-white" data-testid={`lili-action-confirm-${action.id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirmer"}
          </Button>
          <Button size="sm" variant="outline" onClick={cancel} disabled={busy} data-testid={`lili-action-cancel-${action.id}`}>Annuler</Button>
        </div>
      ) : (
        <div className="mt-3 text-xs flex items-center gap-1" data-testid={`lili-action-status-${action.id}`}>
          {action.statut === "executee" && <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Exécutée</>}
          {action.statut === "echec" && <><XCircle className="h-3.5 w-3.5 text-red-600" /> Refusée : {formatApiError(action.resultat?.detail) || `HTTP ${action.http}`}</>}
          {action.statut === "annulee" && <><XCircle className="h-3.5 w-3.5 text-slate-400" /> Annulée</>}
        </div>
      )}
      {tempPwd && (
        <div className="mt-2 text-xs bg-white border border-amber-200 rounded p-2" data-testid={`lili-action-temp-password-${action.id}`}>
          Mot de passe temporaire (affiché une seule fois) : <code className="font-mono">{tempPwd}</code>
        </div>
      )}
    </div>
  );
}

let currentAudio = null;

function ListenButton({ text, id }) {
  const [state, setState] = useState("idle");
  const stop = () => { currentAudio?.pause(); currentAudio = null; setState("idle"); };
  const play = async () => {
    if (state === "playing") return stop();
    currentAudio?.pause();
    setState("loading");
    try {
      const { data } = await api.post("/lili/tts", { text }, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(data));
      currentAudio = audio;
      audio.onended = () => setState("idle");
      await audio.play();
      setState("playing");
    } catch (e) {
      setState("idle");
      toast.error("Lecture vocale indisponible");
    }
  };
  return (
    <button type="button" onClick={play} disabled={state === "loading"} data-testid={`lili-listen-${id}`}
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#17352e] transition-colors">
      {state === "loading" ? <Loader2 className="h-3 w-3 animate-spin" /> : state === "playing" ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
      {state === "playing" ? "Arrêter" : "Écouter"}
    </button>
  );
}

function PdfButton({ d, id }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.get(d.url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${d.nom.replace(/[^\w\- ]+/g, "")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Téléchargement impossible"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid={`lili-pdf-${id}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#17352e] text-[#17352e] text-xs px-3 py-1.5 hover:bg-[#17352e] hover:text-white transition-colors disabled:opacity-60">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}{d.nom} (PDF)
    </button>
  );
}

function DossierButton({ id }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await telechargerDossierInspection(); toast.success("Dossier d'inspection téléchargé"); }
    catch { toast.error("Génération du dossier impossible"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid={`lili-dossier-${id}`}
      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#17352e] text-white text-xs px-3 py-1.5 hover:bg-[#284b40] transition-colors disabled:opacity-60">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}Télécharger le dossier d'inspection (ZIP)
    </button>
  );
}

function Bubble({ m, onActionUpdate, onEdit, onResend, busy, question, sessionId }) {
  if (m.role === "user") {
    return (
      <div className="flex flex-col items-end" data-testid={`lili-user-msg-${m.key}`}>
        <div className="max-w-[80%] bg-[#17352e] text-white rounded-2xl rounded-br-sm px-4 py-2 text-sm whitespace-pre-wrap">
          {m.text}
          {m.pieces?.length > 0 && <div className="mt-2"><PiecesChips pieces={m.pieces} dark testId={`lili-msg-${m.key}`} /></div>}
        </div>
        <MessageTools text={m.text} testId={`lili-msg-${m.key}`} disabled={!m.mid || busy} onEdit={onEdit} onResend={onResend} />
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <div className="h-8 w-8 shrink-0 rounded-full bg-[#C5A059] text-white flex items-center justify-center text-xs font-bold">ZO</div>
      <div className="max-w-[85%] min-w-0">
        {m.tools?.length > 0 && <div className="text-[11px] text-slate-400 mb-1">Consulté : {[...new Set(m.tools)].map((t) => t.replaceAll("_", " ")).join(", ")}</div>}
        {m.text && <div className="text-sm text-slate-800"><AiMarkdown>{stripRetenir(m.text).texte}</AiMarkdown></div>}
        {m.text && !m.streaming && <ListenButton text={stripRetenir(m.text).texte} id={m.key} />}
        {m.text && !m.streaming && <Apprentissage contexte="admin" sessionId={sessionId} messageId={m.mid || String(m.key)} question={question}
          reponse={m.text} propositions={stripRetenir(m.text).propositions} testId={`lili-learn-${m.key}`} />}
        {m.actions?.map((a) => <ActionCard key={a.id} action={a} onUpdate={(na) => onActionUpdate(m.key, na)} />)}
        {m.tools?.includes("preparer_dossier_inspection") && !m.streaming && <DossierButton id={m.key} />}
        {m.downloads?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{m.downloads.map((d, i) => <PdfButton key={`${d.url}-${i}`} d={d} id={`${m.key}-${i}`} />)}</div>}
      </div>
    </div>
  );
}

function VoiceCountdown({ text, onCancel, onEdit, onSend }) {
  const [left, setLeft] = useState(3);
  useEffect(() => {
    if (left <= 0) { onSend(); return undefined; }
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [left, onSend]);
  return (
    <div className="mx-4 mb-2 rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/5 px-3 py-2 text-sm flex items-center gap-3" data-testid="lili-voice-pending">
      <span className="flex-1 min-w-0 truncate">« {text} »</span>
      <span className="text-xs text-slate-500 shrink-0" data-testid="lili-voice-countdown">Envoi dans {left} s</span>
      <Button size="sm" variant="outline" onClick={onEdit} data-testid="lili-voice-edit">Modifier</Button>
      <Button size="sm" variant="outline" onClick={onCancel} data-testid="lili-voice-cancel">Annuler</Button>
    </div>
  );
}

export default function Lili() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceText, setVoiceText] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [pieces, setPieces] = useState([]);
  const inputRef = useRef(null);
  const sessionRef = useRef(null);
  const listRef = useRef(null);

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/lili/sessions/${sid}`);
      const byId = Object.fromEntries(data.actions.map((a) => [a.id, a]));
      setMessages(data.messages.map((m, i) => ({ role: m.role, text: m.text, key: `${sid}-${i}`, tools: [], mid: m.id || m.at, pieces: m.pieces || [],
        actions: (m.actions || []).map((id) => byId[id]).filter(Boolean), downloads: m.downloads || [] })));
      sessionRef.current = sid; setActiveId(sid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const newSession = () => { sessionRef.current = null; setActiveId(null); setMessages([]); setEditing(null); setPieces([]); };

  const patchLast = (fn) => setMessages((ms) => { const c = [...ms]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
  const onActionUpdate = (key, na) => setMessages((ms) => ms.map((m) => (m.key !== key ? m : { ...m, actions: m.actions.map((a) => (a.id === na.id ? na : a)) })));

  const send = async (text, editOf, piecesMsg) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    const jointes = piecesMsg ?? pieces;
    setInput(""); setSending(true); setEditing(null); setPieces([]);
    const key = Date.now();
    setMessages((ms) => {
      const base = editOf ? ms.slice(0, ms.findIndex((x) => x.key === editOf.key)) : ms;
      return [...base, { role: "user", text: message, key: key - 1, pieces: jointes }, { role: "lili", text: "", tools: [], actions: [], key, streaming: true }];
    });
    try {
      await streamLili({ message, sessionId: sessionRef.current, editOf: editOf?.mid, pieces: jointes.map((p) => p.id), onEvent: (ev) => {
        if (ev.type === "session") {
          sessionRef.current = ev.session_id;
          setMessages((ms) => ms.map((x) => (x.key === key - 1 ? { ...x, mid: ev.message_id } : x)));
        }
        else if (ev.type === "delta") patchLast((m) => ({ ...m, text: m.text + ev.text }));
        else if (ev.type === "tool") patchLast((m) => ({ ...m, tools: [...m.tools, ev.name] }));
        else if (ev.type === "action") patchLast((m) => ({ ...m, actions: [...m.actions, ev.action] }));
        else if (ev.type === "download") patchLast((m) => ({ ...m, downloads: [...(m.downloads || []), ev] }));
        else if (ev.type === "error") toast.error(ev.message);
        requestAnimationFrame(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; });
      } });
    } catch (e) { toast.error(e.message || "ZOE indisponible"); }
    finally {
      setMessages((ms) => ms.map((x) => (x.key === key ? { ...x, streaming: false } : x)));
      setSending(false); setActiveId(sessionRef.current); setRefreshKey((k) => k + 1);
    }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-6rem)]">
    <LiliHistory activeId={activeId} onOpen={openSession} onNew={newSession} refreshKey={refreshKey} />
    <div className="flex-1 min-w-0 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="lili-page">
      <div className="px-6 pt-5 border-b border-slate-100">
        <PageHeader title="ZOE — votre assistante O'SCOP GE" subtitle="Administration par simple message. Lecture immédiate ; toute modification attend votre confirmation." />
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-testid="lili-messages">
        {messages.length === 0 && (
          <div className="max-w-xl">
            <div className="flex items-center gap-2 text-[#A88846] font-semibold"><Sparkles className="h-4 w-4" />Bonjour, je suis ZOE.</div>
            <p className="text-sm text-slate-600 mt-1">Je peux consulter et administrer : comptes, entreprises, demandes, courriels, frais, paie, facturation, conventions. Vos droits s'appliquent à chacune de mes actions.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {EXEMPLES.map((ex, i) => (
                <button key={ex} onClick={() => send(ex)} data-testid={`lili-example-${i}`} className="text-xs text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors">{ex}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble key={m.key} m={m} onActionUpdate={onActionUpdate} busy={sending} sessionId={sessionRef.current}
            question={m.role === "user" ? "" : messages.slice(0, i).reverse().find((x) => x.role === "user")?.text}
            onEdit={() => { setEditing(m); setInput(m.text); setPieces(m.pieces || []); setTimeout(() => inputRef.current?.focus(), 0); }}
            onResend={() => send(m.text, m, m.pieces || [])} />
        ))}
        {sending && <div className="text-xs text-slate-400 flex items-center gap-1" data-testid="lili-thinking"><Loader2 className="h-3 w-3 animate-spin" />ZOE réfléchit…</div>}
      </div>
      {voiceText && (
        <VoiceCountdown text={voiceText} onCancel={() => setVoiceText(null)}
                        onEdit={() => { setInput(voiceText); setVoiceText(null); }}
                        onSend={() => { const t = voiceText; setVoiceText(null); send(t); }} />
      )}
      {editing && <EditBanner testId="lili" onCancel={() => { setEditing(null); setInput(""); setPieces([]); }} />}
      {pieces.length > 0 && <div className="px-4 pt-3"><PiecesChips pieces={pieces} testId="lili-compose" onRemove={(p) => (editing ? setPieces((x) => x.filter((y) => y.id !== p.id)) : retirerPiece(p, setPieces))} /></div>}
      <form onSubmit={(e) => { e.preventDefault(); send(undefined, editing || undefined); }} className="border-t border-slate-100 p-4 flex gap-2 items-end">
        <AttachButton contexte="admin" sessionId={sessionRef.current} pieces={pieces} setPieces={setPieces} disabled={sending} testId="lili" />
        <div data-testid="lili-voice"><VoiceRecorder disabled={sending} onTranscribed={(t) => t?.trim() && setVoiceText(t.trim())} /></div>
        <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Demandez à ZOE…  (Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne)"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(undefined, editing || undefined); } }}
                  data-testid="lili-input" className="resize-none" />
        <Button type="submit" disabled={sending || !input.trim()} data-testid="lili-send" className="bg-[#C5A059] hover:bg-[#A88846] text-white self-end h-11 px-5">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? <span className="text-xs font-semibold">Renvoyer</span> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
    </div>
  );
}
